import {Box, Button, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, PencilEdit01Icon} from "@hugeicons/core-free-icons";
import moment from "moment";
import {extensionLabel} from "../../../project/campaigns/hooks/useAmiExtensions";

const formatDate = value => value && moment(value).isValid() ? moment(value).format("DD MMM YYYY") : "Not set";

const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};

export const CampaignCardNew = ({campaign, extension, language, onOpen, onEdit, onDelete}) => (
  <Box
    onClick={() => onOpen(campaign)}
    sx={{
      bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "16px", cursor: "pointer",
      display: "flex", flexDirection: "column", gap: 1.5, p: 2.5, minWidth: 0,
      transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      "&:hover": {boxShadow: "0 2px 8px rgba(35, 38, 77, 0.06)", borderColor: "#D7DBFB"},
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
      <Box sx={{minWidth: 0}}>
        <Button
          onClick={event => {event.stopPropagation(); onOpen(campaign);}}
          sx={{p: 0, minWidth: 0, color: "#23264D", fontSize: 16, fontWeight: 700, textAlign: "left", justifyContent: "flex-start", textTransform: "none", overflowWrap: "anywhere", lineHeight: 1.4}}
        >
          {campaign.name}
        </Button>
        {language && (
          <Box component="span" sx={{
            display: "inline-block", mt: 0.5, px: 0.9, py: 0.25, borderRadius: "999px", fontSize: 11,
            fontWeight: 700, letterSpacing: "0.02em", color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)",
          }}>
            {language}
          </Box>
        )}
        {campaign.description && (
          <Typography sx={{mt: 0.75, fontSize: 13, color: "#6B6B80", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere"}}>
            {campaign.description}
          </Typography>
        )}
      </Box>
      <Stack direction="row" sx={{flexShrink: 0}}>
        <Tooltip title="Edit campaign">
          <IconButton size="small" aria-label={`Edit ${campaign.name}`} onClick={event => {event.stopPropagation(); onEdit(campaign);}} sx={iconButtonSx}>
            <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete campaign">
          <IconButton size="small" aria-label={`Delete ${campaign.name}`} onClick={event => {event.stopPropagation(); onDelete(campaign);}} sx={{...iconButtonSx, "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"}}}>
            <HugeiconsIcon icon={Delete02Icon} size={16}/>
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>

    <Stack gap={1} sx={{color: "#6B6B80"}}>
      <Stack direction="row" alignItems="center" gap={1}>
        <CalendarTodayOutlinedIcon sx={{fontSize: 15, flexShrink: 0}}/>
        <Typography sx={{fontSize: 12}}>{formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <AccessTimeIcon sx={{fontSize: 15, flexShrink: 0}}/>
        <Typography sx={{fontSize: 12}}>Calling hours: {campaign.startTime || "Not set"} – {campaign.endTime || "Not set"}</Typography>
      </Stack>
    </Stack>

    <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, p: 1.25, borderRadius: "12px", bgcolor: "#FAFAFD"}}>
      <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
        <PhoneOutlinedIcon sx={{fontSize: 16, color: "#8A8AA0"}}/>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 10, color: "#8A8AA0"}}>FROM NUMBER</Typography>
          <Typography noWrap title={campaign.fromNumber || "Not set"} sx={{fontSize: 12, fontWeight: 700, color: "#23264D"}}>{campaign.fromNumber || "Not set"}</Typography>
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
        <TuneOutlinedIcon sx={{fontSize: 16, color: "#8A8AA0"}}/>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 10, color: "#8A8AA0"}}>EXTENSION</Typography>
          <Typography noWrap title={extensionLabel(extension) || "Not set"} sx={{fontSize: 12, fontWeight: 700, color: "#23264D"}}>{extensionLabel(extension) || "Not set"}</Typography>
        </Box>
      </Stack>
    </Box>

    <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1}}>
      {[
        ["Numbers", campaign.totalPhoneNumbers ?? 0],
        ["Answered", campaign.statusCounts?.ANSWERED ?? 0],
        ["Failed", campaign.statusCounts?.FAILED ?? 0],
      ].map(([label, value]) => (
        <Box key={label}>
          <Typography sx={{fontSize: 22, fontWeight: 700, color: "#23264D", lineHeight: 1.1}}>{value}</Typography>
          <Typography sx={{fontSize: 10, color: "#8A8AA0", textTransform: "uppercase", letterSpacing: "0.04em"}}>{label}</Typography>
        </Box>
      ))}
    </Box>

    <Typography sx={{fontSize: 11, color: "#8A8AA0", pt: 0.5}}>
      Created {formatDate(campaign.dateCreated)}
    </Typography>
  </Box>
);
